import React, { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Plus, Minus, Trash2, Pill, Package, AlertCircle, AlertTriangle, Search, X, Loader2, RotateCw } from 'lucide-react';
import {
    getDoctorMedicines,
    createDoctorMedicine,
    getPrescriptionsByAppointment,
    createPrescription,
    updatePrescription,
    deletePrescription,
} from '../../redux/medicineSlice';
import {
    serverReason,
    toDose,
    formatDose,
    cleanClinicalText,
    DOSE_STEP,
    MAX_DOSE,
} from './prescriptionHelpers';
import DraftChip from '../common/DraftChip';
import useDraftAutosave from '../../hooks/useDraftAutosave';
import { fetchDrafts, PRESCRIPTION_DRAFT_SECTION } from '../../redux/appointmentDraftSlice';

const MAX_DURATION = 365;
const DEFAULT_DURATION = 7;
const COUNT_FIELDS = ['morningCount', 'afternoonCount', 'eveningCount', 'nightCount'];
// The picker searches the doctor's catalogue on the server; this is how long
// it waits after the last keystroke.
const PICKER_DEBOUNCE_MS = 300;

// Dose counter in quarter steps: 0 → ¼ → ½ → ¾ → 1 → 1¼ … up to 10.
const Counter = ({ value, onChange, disabled, label }) => {
    const n = toDose(value);
    return (
        <div className="flex items-center justify-center gap-1">
            <button
                type="button"
                data-testid="patient-prescription-counter-decrement-button"
                disabled={disabled || n <= 0}
                onClick={() => onChange(toDose(n - DOSE_STEP))}
                aria-label={label ? `${label} −` : undefined}
                className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
                <Minus size={12} />
            </button>
            <span className="min-w-7 text-center text-sm font-medium text-gray-700 tabular-nums" aria-label={label}>{formatDose(n)}</span>
            <button
                type="button"
                data-testid="patient-prescription-counter-increment-button"
                disabled={disabled || n >= MAX_DOSE}
                onClick={() => onChange(Math.min(MAX_DOSE, toDose(n + DOSE_STEP)))}
                aria-label={label ? `${label} +` : undefined}
                className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
                <Plus size={12} />
            </button>
        </div>
    );
};

const EMPTY_INLINE_FORM = { name: '', type: 'medicine', brand: '', dose: '' };

function PrescriptionSection({ appointment, readOnly = false, draftControlRef = null }) {
    const { t, i18n } = useTranslation(['prescriptionSection']);
    const lang = i18n?.language || 'en';
    const dispatch = useDispatch();

    const doctorId = appointment?.doctorId;
    const patientId = appointment?.patientId;
    const appointmentId = appointment?.appointmentId;

    const {
        medicines = [],
        loading: catalogueLoading,
        loadingMore: catalogueLoadingMore,
        medicinesPage = 0,
        medicinesTotalPages = 0,
        medicinesError,
        loadMoreError,
        prescriptions = [],
        prescriptionsFor,
        prescriptionsLoading,
        prescriptionsError,
        medicinesSource = 'manual',
    } = useSelector((state) => state.medicine);
    // Where the tenant has its doctors pick from the pharmacy ONLY (owner,
    // 12 Oct 2026), a medicine cannot be typed into the list from here — the
    // server refuses it; the doctor picks it on the Medicines page.
    const canTypeMedicine = medicinesSource !== 'pharmacy';

    const [addMode, setAddMode] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    // The search text the catalogue was last asked for — the picker only calls
    // a search "no match" once the answer is for what is in the box.
    const [requestedSearch, setRequestedSearch] = useState(null);
    const [inlineForm, setInlineForm] = useState(null);
    const [inlineError, setInlineError] = useState('');
    const [inlineSaving, setInlineSaving] = useState(false);
    const [removingAll, setRemovingAll] = useState(false);
    const [dropdownPos, setDropdownPos] = useState(null);
    // A medicine picked from the catalogue is a DRAFT row: it lives only here
    // until it has a dose time (or T-mode instructions) and the doctor saves.
    // Nothing doseless is ever written to the prescription.
    const [newRows, setNewRows] = useState([]);
    // Row-level edits (counter +/-, food timing, S/T toggle, days, notes)
    // buffer here keyed by row key (prescriptionId, or the draft id) — the API
    // is only hit when the user clicks Save Changes.
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);
    // Days / notes inputs additionally use short-lived "typing buffers" so a
    // transient value ("1" → "12") does not flicker through `drafts`.
    const [daysDraft, setDaysDraft] = useState({});
    const [notesDraft, setNotesDraft] = useState({});
    const pickerRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Switching appointment starts clean: no drafts, no picker, and the slice
    // empties the table the moment the new fetch goes out.
    useEffect(() => {
        setNewRows([]);
        setDrafts({});
        setDaysDraft({});
        setNotesDraft({});
        setAddMode(false);
        setAddSearch('');
        setInlineForm(null);
        if (appointmentId) {
            dispatch(getPrescriptionsByAppointment(appointmentId));
        }
    }, [appointmentId, dispatch]);

    const retryList = () => {
        if (appointmentId) dispatch(getPrescriptionsByAppointment(appointmentId));
    };

    const trimmedSearch = addSearch.trim();

    const fetchCatalogue = (page = 1) => {
        if (!doctorId) return;
        setRequestedSearch(trimmedSearch);
        dispatch(getDoctorMedicines({ doctorId, search: trimmedSearch, page }));
    };

    // Server-side catalogue search: asked when the picker opens and again
    // (debounced) as the doctor types.
    useEffect(() => {
        if (!addMode || !doctorId || readOnly) return undefined;
        const timer = setTimeout(() => {
            setRequestedSearch(trimmedSearch);
            dispatch(getDoctorMedicines({ doctorId, search: trimmedSearch, page: 1 }));
        }, trimmedSearch ? PICKER_DEBOUNCE_MS : 0);
        return () => clearTimeout(timer);
    }, [addMode, trimmedSearch, doctorId, readOnly, dispatch]);

    const closePicker = () => {
        setAddMode(false);
        setAddSearch('');
        setInlineForm(null);
        setInlineError('');
    };

    // Close the inline picker when clicking outside both the input wrapper and
    // the portal-rendered dropdown.
    useEffect(() => {
        if (!addMode) return undefined;
        const handler = (e) => {
            const inInput = pickerRef.current && pickerRef.current.contains(e.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inInput && !inDropdown) closePicker();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addMode]);

    useEffect(() => {
        if (addMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [addMode]);

    // Position the portal dropdown above/below the input, recompute on scroll /
    // resize so the dropdown follows the input if the page scrolls.
    useLayoutEffect(() => {
        if (!addMode) {
            setDropdownPos(null);
            return undefined;
        }
        const update = () => {
            const el = inputRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const margin = 8;
            const spaceAbove = rect.top - margin;
            const spaceBelow = window.innerHeight - rect.bottom - margin;
            const openUp = spaceAbove >= spaceBelow;
            const maxHeight = Math.min(320, openUp ? spaceAbove : spaceBelow);
            setDropdownPos({
                left: rect.left,
                width: Math.max(rect.width, 288),
                openUp,
                top: openUp ? undefined : rect.bottom + 4,
                bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
                maxHeight,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [addMode, addSearch, inlineForm]);

    // ---- what is on screen ------------------------------------------------

    // Rows in the slice belong to ONE appointment; never render another's.
    const belongsHere = !!appointmentId && prescriptionsFor === appointmentId;
    const listFailed = belongsHere && !prescriptionsLoading && !!prescriptionsError;
    const listLoading = !!appointmentId && !listFailed && (!belongsHere || !!prescriptionsLoading);
    const persistedRows = useMemo(
        () => (belongsHere && !prescriptionsError ? prescriptions : []).map((p) => ({ ...p, _key: p.prescriptionId, _isNew: false })),
        [belongsHere, prescriptionsError, prescriptions]
    );
    const rows = useMemo(
        () => [...persistedRows, ...newRows.map((d) => ({ ...d, _key: d.draftId, _isNew: true }))],
        [persistedRows, newRows]
    );

    const alreadyAddedIds = useMemo(
        () => new Set(rows.map((r) => r.medicineId).filter(Boolean)),
        [rows]
    );
    const availableMaster = useMemo(
        () => medicines.filter((m) => !alreadyAddedIds.has(m.medicineId)),
        [medicines, alreadyAddedIds]
    );

    // Read what a field would be if the user saved right now: the drafted
    // override if set, otherwise the value on the row.
    const valueOf = (row, field) => {
        const d = drafts[row._key];
        return d && Object.prototype.hasOwnProperty.call(d, field) ? d[field] : row[field];
    };
    const modeOf = (row) => (valueOf(row, 'entryMode') === 'T' ? 'T' : 'S');
    const notesNow = (row) => (notesDraft[row._key] !== undefined
        ? cleanClinicalText(notesDraft[row._key])
        : (valueOf(row, 'notes') || ''));

    // A medicine line is complete once it says WHEN to take it: a dose at some
    // time of day, or written instructions in T mode. A product (a collar, a
    // bandage) has no dose schedule. Same rule as the server's
    // DOSE_SCHEDULE_REQUIRED.
    const isComplete = (row) => {
        if ((valueOf(row, 'type') || 'medicine') !== 'medicine') return true;
        if (COUNT_FIELDS.some((f) => toDose(valueOf(row, f)) > 0)) return true;
        return modeOf(row) === 'T' && notesNow(row).trim().length > 0;
    };

    // Once the visit is Completed its saved lines are locked (the server
    // answers 409 PRESCRIPTION_LOCKED to an edit or delete); new lines may
    // still be added.
    const savedLocked = appointment?.status === 'Completed';
    const dirtyPersisted = savedLocked ? [] : persistedRows.filter((r) => !!drafts[r._key]);
    const pendingRows = [...dirtyPersisted, ...rows.filter((r) => r._isNew)];
    const pendingCount = pendingRows.length;
    const incompleteRows = pendingRows.filter((r) => !isComplete(r));
    const listBlocked = listFailed || listLoading;
    const canSave = !saving && !listBlocked && pendingCount > 0 && incompleteRows.length === 0;

    // Drop drafts for rows that no longer exist (deleted, refreshed away) so
    // the pending count doesn't lie.
    useEffect(() => {
        if (Object.keys(drafts).length === 0) return;
        const liveIds = new Set(rows.map((r) => r._key));
        const stale = Object.keys(drafts).filter((id) => !liveIds.has(id));
        if (stale.length === 0) return;
        setDrafts((prev) => {
            const next = { ...prev };
            stale.forEach((id) => { delete next[id]; });
            return next;
        });
    }, [rows, drafts]);

    // ---- editing ------------------------------------------------------------

    const setDraft = (row, patch) => {
        setDrafts((prev) => ({ ...prev, [row._key]: { ...(prev[row._key] || {}), ...patch } }));
    };

    const dropKeys = (setter, keys) => setter((prev) => {
        const next = { ...prev };
        keys.forEach((k) => { delete next[k]; });
        return next;
    });

    const handlePick = (item) => {
        if (listBlocked) return;
        setNewRows((prev) => (prev.some((r) => r.medicineId === item.medicineId) ? prev : [...prev, {
            draftId: `new-${item.medicineId}`,
            medicineId: item.medicineId,
            medicineName: item.name,
            brand: item.brand || null,
            dose: item.dose || null,
            type: item.type || 'medicine',
            isImportant: !!item.isImportant,
            morningCount: 0,
            afternoonCount: 0,
            eveningCount: 0,
            nightCount: 0,
            foodTiming: 'AF',
            durationDays: DEFAULT_DURATION,
            entryMode: 'S',
            notes: null,
        }]));
        closePicker();
    };

    // P31 — the medicine is not in the catalogue: add it there and put it on
    // this prescription as a draft, without leaving the appointment.
    const openInlineAdd = () => {
        setInlineError('');
        setInlineForm({ ...EMPTY_INLINE_FORM, name: cleanClinicalText(trimmedSearch).slice(0, 100) });
    };

    const submitInlineAdd = async (e) => {
        e?.preventDefault?.();
        if (!inlineForm || inlineSaving) return;
        const name = cleanClinicalText(inlineForm.name).trim();
        if (!name) {
            setInlineError(t('prescriptionSection.inline_name_required', 'Enter the medicine name.'));
            return;
        }
        setInlineSaving(true);
        setInlineError('');
        try {
            const isProduct = inlineForm.type === 'product';
            const res = await dispatch(createDoctorMedicine({
                doctorId,
                name,
                brand: cleanClinicalText(inlineForm.brand).trim(),
                dose: isProduct ? '' : cleanClinicalText(inlineForm.dose).trim(),
                description: '',
                purpose: '',
                isImportant: false,
                type: inlineForm.type,
            }));
            if (createDoctorMedicine.fulfilled.match(res) && res.payload?.data) {
                handlePick(res.payload.data);
                toast.success(t('prescriptionSection.inline_added', '“{{name}}” was added to your catalogue. Set when to take it, then save.', { name }));
            } else {
                setInlineError(serverReason(res.payload, t('prescriptionSection.inline_add_failed', 'The medicine could not be added to your catalogue.'), lang));
            }
        } finally {
            setInlineSaving(false);
        }
    };

    // What a row would send if saved now — drafted fields plus anything still
    // sitting in a typing buffer (a notes box or days box not yet left).
    const patchFor = (row) => {
        const patch = { ...(drafts[row._key] || {}) };
        const rawNotes = notesDraft[row._key];
        if (rawNotes !== undefined) {
            const cleaned = cleanClinicalText(rawNotes);
            if (cleaned !== (valueOf(row, 'notes') || '')) patch.notes = cleaned || null;
        }
        const rawDays = daysDraft[row._key];
        if (rawDays !== undefined && rawDays !== '') {
            const n = parseInt(rawDays, 10);
            if (!Number.isNaN(n)) {
                const clamped = Math.min(MAX_DURATION, Math.max(1, n));
                if (clamped !== (valueOf(row, 'durationDays') ?? DEFAULT_DURATION)) patch.durationDays = clamped;
            }
        }
        return patch;
    };

    const createPayloadFor = (row) => {
        const merged = { ...row, ...patchFor(row) };
        return {
            appointmentId,
            doctorId,
            patientId,
            medicineId: merged.medicineId,
            medicineName: merged.medicineName,
            brand: merged.brand || null,
            dose: merged.dose || null,
            type: merged.type || 'medicine',
            isImportant: !!merged.isImportant,
            morningCount: toDose(merged.morningCount),
            afternoonCount: toDose(merged.afternoonCount),
            eveningCount: toDose(merged.eveningCount),
            nightCount: toDose(merged.nightCount),
            foodTiming: merged.foodTiming || 'AF',
            durationDays: merged.durationDays ?? DEFAULT_DURATION,
            entryMode: merged.entryMode === 'T' ? 'T' : 'S',
            notes: merged.notes || null,
        };
    };

    // ---- visit draft (round 9) -----------------------------------------------
    // Everything not yet saved — picked medicines, edited cells, a notes or
    // days box still being typed in — is kept as ONE server-side draft
    // ("prescription"), 2 s after the last change, and put back when the visit
    // is opened again. Only for the visit's own doctor, in edit mode.
    const myId = typeof localStorage !== 'undefined' ? localStorage.getItem('ID') : null;
    const draftEnabled = !readOnly
        && typeof localStorage !== 'undefined' && localStorage.getItem('Role') === 'doctor'
        && !!appointmentId && !!doctorId && String(doctorId) === String(myId);
    const rxDraft = useDraftAutosave({ appointmentId: draftEnabled ? appointmentId : null, enabled: draftEnabled });
    const draftsState = useSelector((state) => state.appointmentDraft);
    // The serialised draft last sent (or restored): a change is scheduled only
    // when the pending set actually differs from it.
    const lastDraftKeyRef = useRef('[]');
    // Set by a restore: the state it applies is the draft itself, not an edit
    // to send back (and re-sending it would turn the amber chip grey).
    const skipNextDraftRef = useRef(false);
    const restoredForRef = useRef(null);

    // CANONICAL shape, shared with the Pro-App so a draft typed on one carries
    // over to the other: an array of lines; a line WITHOUT `prescriptionId`
    // is an unsaved new line with ALL its fields (the prescription POST
    // body's names — the ids are the draft's own URL); a line WITH it is an
    // edit carrying only the changed fields.
    const draftContent = useMemo(() => {
        const lines = [];
        rows.forEach((row) => {
            const patch = patchFor(row);
            if (row._isNew) {
                const { appointmentId: _a, doctorId: _d, patientId: _p, ...line } = createPayloadFor(row);
                lines.push(line);
            } else if (!savedLocked && Object.keys(patch).length > 0) {
                lines.push({ prescriptionId: row.prescriptionId, ...patch });
            }
        });
        return lines;
        // patchFor reads drafts / notesDraft / daysDraft, listed here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, drafts, notesDraft, daysDraft, savedLocked]);

    useEffect(() => {
        if (draftEnabled && appointmentId) dispatch(fetchDrafts({ appointmentId }));
    }, [draftEnabled, appointmentId, dispatch]);

    // A new visit starts from "nothing pending", so its first edit is news.
    useEffect(() => {
        lastDraftKeyRef.current = '[]';
    }, [appointmentId]);

    useEffect(() => {
        if (!draftEnabled) return;
        const key = JSON.stringify(draftContent);
        if (key === lastDraftKeyRef.current) return;
        lastDraftKeyRef.current = key;
        if (skipNextDraftRef.current) {
            skipNextDraftRef.current = false;
            return;
        }
        rxDraft.schedule(PRESCRIPTION_DRAFT_SECTION, draftContent, { clean: draftContent.length === 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftContent, draftEnabled]);

    // Restore once per visit, after both the draft and the saved list are in:
    // a saved row's edit can only be re-applied to a row that still exists,
    // and a picked medicine already saved since is not re-added.
    useEffect(() => {
        if (!draftEnabled || !belongsHere || prescriptionsLoading || prescriptionsError) return;
        if (!draftsState?.loaded || draftsState.appointmentId !== appointmentId) return;
        if (restoredForRef.current === appointmentId) return;
        restoredForRef.current = appointmentId;
        const draft = draftsState.drafts?.[PRESCRIPTION_DRAFT_SECTION];
        const items = Array.isArray(draft?.content) ? draft.content : [];
        if (items.length === 0) return;
        const savedIds = new Set(persistedRows.map((r) => r.prescriptionId));
        const savedMedicines = new Set(persistedRows.map((r) => r.medicineId));
        const restoredNew = [];
        const restoredEdits = {};
        // Defensive: the first web build (never shipped) wrote
        // { kind, row, patch } items — read them as canonical lines.
        const lines = items.map((item) => {
            if (item?.kind === 'new' && item.row) return { ...item.row, ...(item.patch || {}), prescriptionId: undefined };
            if (item?.kind === 'edit') return { prescriptionId: item.prescriptionId, ...(item.patch || {}) };
            return item;
        });
        lines.forEach((line) => {
            if (!line || typeof line !== 'object') return;
            if (line.prescriptionId) {
                const { prescriptionId, ...patch } = line;
                if (!savedLocked && savedIds.has(prescriptionId) && Object.keys(patch).length > 0) {
                    restoredEdits[prescriptionId] = patch;
                }
            } else if (line.medicineId && !savedMedicines.has(line.medicineId)
                && !restoredNew.some((r) => r.medicineId === line.medicineId)) {
                const { prescriptionId: _unused, draftId: _old, ...fields } = line;
                restoredNew.push({
                    ...fields,
                    // The screen's own key for an unsaved row (see handlePick).
                    draftId: `new-${line.medicineId}`,
                    type: fields.type || 'medicine',
                    foodTiming: fields.foodTiming || 'AF',
                    durationDays: fields.durationDays ?? DEFAULT_DURATION,
                    entryMode: fields.entryMode === 'T' ? 'T' : 'S',
                });
            }
        });
        if (restoredNew.length === 0 && Object.keys(restoredEdits).length === 0) return;
        skipNextDraftRef.current = true;
        setNewRows(restoredNew);
        setDrafts(restoredEdits);
        rxDraft.markRestored(PRESCRIPTION_DRAFT_SECTION, draft.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftEnabled, belongsHere, prescriptionsLoading, prescriptionsError, draftsState, appointmentId, persistedRows, savedLocked]);

    // Returns true when every pending line was saved (the complete-visit
    // prompt in MedicalHistory waits on it).
    const handleSaveAll = async () => {
        if (!canSave) return false;
        setSaving(true);
        const jobs = pendingRows.map((row) => (row._isNew
            ? { row, thunk: createPrescription, run: () => dispatch(createPrescription(createPayloadFor(row))) }
            : { row, thunk: updatePrescription, run: () => dispatch(updatePrescription({ prescriptionId: row.prescriptionId, payload: patchFor(row) })) }));
        try {
            const results = await Promise.all(jobs.map((j) => j.run()));
            const failed = [];
            const savedKeys = [];
            results.forEach((r, idx) => {
                if (jobs[idx].thunk.fulfilled.match(r)) savedKeys.push(jobs[idx].row._key);
                else failed.push(r);
            });
            if (savedKeys.length) {
                const saved = new Set(savedKeys);
                setNewRows((prev) => prev.filter((d) => !saved.has(d.draftId)));
                dropKeys(setDrafts, savedKeys);
                dropKeys(setNotesDraft, savedKeys);
                dropKeys(setDaysDraft, savedKeys);
            }
            if (failed.length === 0) {
                // Committed: the draft is gone, and the now-empty pending set
                // is not news to send again.
                lastDraftKeyRef.current = '[]';
                rxDraft.committed(PRESCRIPTION_DRAFT_SECTION);
                toast.success(t('prescriptionSection.save_success', 'Changes saved'));
                return true;
            }
            const reason = serverReason(failed[0].payload, '', lang);
            if (failed.length === results.length) {
                toast.error(reason || t('prescriptionSection.save_failed', 'Failed to save changes'));
            } else if (reason) {
                toast.warn(t('prescriptionSection.save_partial_reason', '{{n}} row(s) could not be saved: {{reason}}', { n: failed.length, reason }));
            } else {
                toast.warn(t('prescriptionSection.save_partial', '{{n}} change(s) could not be saved', { n: failed.length }));
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    // ---- complete-visit hand-off (owner, 24 Sep 2026) -------------------------
    // MedicalHistory asks before completing a visit while lines are still only
    // pending here, then commits EVERY section in one request:
    //   hasPendingDraft()  whether to ask at all;
    //   pendingPayload()   `{ create, update }` in the canonical draft shape
    //                      (create = a full new line, update = prescriptionId +
    //                      the changed fields), or null — after an error toast —
    //                      when a line cannot be saved (no dose time yet): an
    //                      error, not a skip;
    //   markCommitted()    the commit landed: forget the pending lines and the
    //                      chip (the server deleted the draft) and re-read the
    //                      saved list;
    //   savePending()      this section's own Save (kept for other callers);
    //   flushDraft()       "Keep as draft" on leaving: send the pending
    //                      debounced draft write now;
    //   pendingKey()       a stable serialisation of the pending lines (the
    //                      canonical draft) — MedicalHistory's "acknowledged
    //                      draft" baseline compares it to ask only once.
    const pendingPayload = () => {
        if (readOnly || pendingCount === 0) return { create: [], update: [] };
        if (listBlocked || saving) {
            toast.error(t('prescriptionSection.save_failed', 'Failed to save changes'));
            return null;
        }
        if (incompleteRows.length > 0) {
            toast.error(t('prescriptionSection.dose_required', 'Set at least one dose time — or switch to T and write the instructions — for: {{names}}', {
                names: incompleteRows.map((r) => r.medicineName).join(', '),
            }));
            return null;
        }
        const create = [];
        const update = [];
        pendingRows.forEach((row) => {
            if (row._isNew) {
                const { appointmentId: _a, doctorId: _d, patientId: _p, ...line } = createPayloadFor(row);
                create.push(line);
            } else {
                const patch = patchFor(row);
                if (Object.keys(patch).length > 0) update.push({ prescriptionId: row.prescriptionId, ...patch });
            }
        });
        return { create, update };
    };

    const markCommitted = () => {
        setNewRows([]);
        setDrafts({});
        setDaysDraft({});
        setNotesDraft({});
        lastDraftKeyRef.current = '[]';
        rxDraft.clearLocal(PRESCRIPTION_DRAFT_SECTION);
        if (appointmentId) dispatch(getPrescriptionsByAppointment(appointmentId));
    };

    useImperativeHandle(draftControlRef, () => ({
        hasPendingDraft: () => !readOnly && pendingCount > 0,
        pendingPayload,
        markCommitted,
        flushDraft: () => rxDraft.flushAll(),
        pendingKey: () => JSON.stringify(draftContent),
        savePending: async () => {
            if (readOnly || pendingCount === 0) return true;
            if (pendingPayload() === null) return false;
            return handleSaveAll();
        },
    }));

    const handleDiscardAll = () => {
        if (saving || pendingCount === 0) return;
        setDrafts({});
        setDaysDraft({});
        setNotesDraft({});
        setNewRows([]);
        // Discarding the changes discards their draft too.
        lastDraftKeyRef.current = '[]';
        rxDraft.discard(PRESCRIPTION_DRAFT_SECTION);
    };

    // The chip's "Discard draft" — same outcome, reachable even when the only
    // thing pending is text still in a typing buffer.
    const handleDiscardDraft = () => {
        if (saving) return;
        setDrafts({});
        setDaysDraft({});
        setNotesDraft({});
        setNewRows([]);
        lastDraftKeyRef.current = '[]';
        rxDraft.discard(PRESCRIPTION_DRAFT_SECTION);
    };

    const handleRemove = async (row) => {
        if (row._isNew) {
            // Never saved — nothing to ask about and nothing to delete.
            setNewRows((prev) => prev.filter((d) => d.draftId !== row._key));
            dropKeys(setDrafts, [row._key]);
            dropKeys(setNotesDraft, [row._key]);
            dropKeys(setDaysDraft, [row._key]);
            return;
        }
        const ok = await Swal.fire({
            title: t('prescriptionSection.confirm_remove_title', 'Remove medicine?'),
            text: t('prescriptionSection.confirm_remove_text', 'This will remove "{{name}}" from this prescription.', { name: row.medicineName }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('prescriptionSection.remove_confirm', 'Yes, remove'),
            cancelButtonText: t('prescriptionSection.cancel', 'Cancel'),
        });
        if (!ok.isConfirmed) return;
        const res = await dispatch(deletePrescription(row.prescriptionId));
        if (deletePrescription.fulfilled.match(res)) {
            toast.success(t('prescriptionSection.removed', 'Medicine removed'));
        } else {
            toast.error(serverReason(res.payload, t('prescriptionSection.remove_failed', 'Remove failed'), lang));
        }
    };

    const handleRemoveAll = async () => {
        if (!rows.length || removingAll) return;
        const ok = await Swal.fire({
            title: t('prescriptionSection.confirm_remove_all_title', 'Remove all medicines?'),
            text: t('prescriptionSection.confirm_remove_all_text', 'This will clear every medicine from this prescription.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('prescriptionSection.remove_all_confirm', 'Yes, remove all'),
            cancelButtonText: t('prescriptionSection.cancel', 'Cancel'),
        });
        if (!ok.isConfirmed) return;

        setNewRows([]);
        setRemovingAll(true);
        try {
            const results = await Promise.all(
                (savedLocked ? [] : persistedRows).map((p) => dispatch(deletePrescription(p.prescriptionId)))
            );
            const failedResults = results.filter((r) => !deletePrescription.fulfilled.match(r));
            const failed = failedResults.length;
            if (failed === 0) {
                toast.success(t('prescriptionSection.removed_all', 'All medicines removed'));
            } else if (failed === results.length) {
                toast.error(serverReason(failedResults[0].payload, t('prescriptionSection.remove_all_failed', 'Failed to remove medicines'), lang));
            } else {
                toast.warn(t('prescriptionSection.remove_all_partial', '{{n}} medicines could not be removed', { n: failed }));
            }
        } finally {
            setRemovingAll(false);
        }
    };

    const commitNotesChange = (row) => {
        const raw = notesDraft[row._key];
        dropKeys(setNotesDraft, [row._key]);
        if (raw === undefined) return;
        const next = cleanClinicalText(raw);
        const current = valueOf(row, 'notes') || '';
        if (next === current) return;
        setDraft(row, { notes: next || null });
    };

    const commitDaysChange = (row) => {
        const raw = daysDraft[row._key];
        // Clear the typing buffer regardless so the cell reverts to the
        // drafted (or persisted) value on bad input or no-op.
        dropKeys(setDaysDraft, [row._key]);
        if (raw === undefined || raw === '') return;
        const n = parseInt(raw, 10);
        if (Number.isNaN(n)) return;
        const clamped = Math.min(MAX_DURATION, Math.max(1, n));
        if (clamped === (valueOf(row, 'durationDays') ?? DEFAULT_DURATION)) return;
        setDraft(row, { durationDays: clamped });
    };

    const colCount = readOnly ? 7 : 8;
    const catalogueHasMore = medicinesPage > 0 && medicinesPage < medicinesTotalPages;
    const searchSettled = requestedSearch !== null && requestedSearch === trimmedSearch && !catalogueLoading;

    // Hide the whole section when the super-admin Medicines feature is off, and —
    // in read-only (completed) view — when no medicines/products were added
    // (avoids an empty "Treatment Plan – Medicines" table). A failed load is
    // never hidden: the doctor must see that the list is unknown.
    if (readOnly && !listLoading && !listFailed && rows.length === 0) return null;

    const countLabels = {
        morningCount: t('prescriptionSection.col_morning', 'Morning'),
        afternoonCount: t('prescriptionSection.col_afternoon', 'Afternoon'),
        eveningCount: t('prescriptionSection.col_evening', 'Evening'),
        nightCount: t('prescriptionSection.col_night', 'Night'),
    };

    const renderPickerBody = () => {
        if (inlineForm) {
            const isProduct = inlineForm.type === 'product';
            return (
                <form onSubmit={submitInlineAdd} className="p-3 space-y-2" data-testid="prescription-inline-add-form">
                    <p className="text-xs font-semibold text-gray-700">
                        {t('prescriptionSection.inline_form_title', 'Add to your catalogue')}
                    </p>
                    <div className="flex gap-1.5">
                        {['medicine', 'product'].map((tp) => (
                            <button
                                key={tp}
                                type="button"
                                onClick={() => setInlineForm((f) => ({ ...f, type: tp }))}
                                disabled={inlineSaving}
                                data-testid={`prescription-inline-add-type-${tp}`}
                                className={`flex-1 px-2 py-1 rounded-lg border text-xs font-medium transition cursor-pointer ${inlineForm.type === tp ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD]'}`}
                            >
                                {tp === 'product'
                                    ? t('prescriptionSection.inline_type_product', 'Product')
                                    : t('prescriptionSection.inline_type_medicine', 'Medicine')}
                            </button>
                        ))}
                    </div>
                    <label className="block text-[11px] font-medium text-gray-500">
                        {t('prescriptionSection.inline_name_label', 'Name')}
                        <input
                            type="text"
                            value={inlineForm.name}
                            maxLength={100}
                            onChange={(e) => setInlineForm((f) => ({ ...f, name: cleanClinicalText(e.target.value) }))}
                            disabled={inlineSaving}
                            data-testid="prescription-inline-add-name"
                            className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
                        />
                    </label>
                    <div className={`grid gap-2 ${isProduct ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <label className="block text-[11px] font-medium text-gray-500">
                            {t('prescriptionSection.inline_brand_label', 'Brand (optional)')}
                            <input
                                type="text"
                                value={inlineForm.brand}
                                maxLength={80}
                                onChange={(e) => setInlineForm((f) => ({ ...f, brand: cleanClinicalText(e.target.value) }))}
                                disabled={inlineSaving}
                                data-testid="prescription-inline-add-brand"
                                className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
                            />
                        </label>
                        {!isProduct && (
                            <label className="block text-[11px] font-medium text-gray-500">
                                {t('prescriptionSection.inline_dose_label', 'Strength (optional)')}
                                <input
                                    type="text"
                                    value={inlineForm.dose}
                                    maxLength={50}
                                    placeholder="500mg"
                                    onChange={(e) => setInlineForm((f) => ({ ...f, dose: cleanClinicalText(e.target.value) }))}
                                    disabled={inlineSaving}
                                    data-testid="prescription-inline-add-dose"
                                    className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
                                />
                            </label>
                        )}
                    </div>
                    {inlineError && (
                        <p role="alert" className="text-xs text-red-600" data-testid="prescription-inline-add-error">{inlineError}</p>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            type="button"
                            onClick={() => { setInlineForm(null); setInlineError(''); }}
                            disabled={inlineSaving}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                            data-testid="prescription-inline-add-cancel"
                        >
                            {t('prescriptionSection.cancel', 'Cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={inlineSaving || !inlineForm.name.trim()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs font-semibold shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="prescription-inline-add-submit"
                        >
                            {inlineSaving && <Loader2 size={12} className="animate-spin" />}
                            {t('prescriptionSection.inline_add_submit', 'Add and prescribe')}
                        </button>
                    </div>
                </form>
            );
        }

        if (medicinesError && !catalogueLoading && medicinesError?.httpStatus === 403) {
            // Not an outage — this login may not read this catalogue. Say why,
            // without an error style or a Retry that could never succeed.
            return (
                <p className="p-4 text-center text-xs text-gray-500" data-testid="prescription-picker-forbidden">
                    {serverReason(medicinesError, t('prescriptionSection.picker_forbidden', 'You cannot pick from this doctor’s medicine catalogue.'), lang)}
                </p>
            );
        }

        if (medicinesError && !catalogueLoading) {
            return (
                <div role="alert" className="p-4 text-center space-y-2" data-testid="prescription-picker-error">
                    <p className="text-xs text-red-600">
                        {serverReason(medicinesError, t('prescriptionSection.picker_load_failed', 'Your medicines could not be loaded.'), lang)}
                    </p>
                    <button
                        type="button"
                        onClick={() => fetchCatalogue(1)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50 cursor-pointer"
                        data-testid="prescription-picker-retry"
                    >
                        <RotateCw size={12} /> {t('prescriptionSection.retry', 'Retry')}
                    </button>
                </div>
            );
        }

        if (catalogueLoading && (medicines.length === 0 || !searchSettled)) {
            return (
                <p className="flex items-center justify-center gap-2 text-xs text-gray-500 py-4" data-testid="prescription-picker-loading">
                    <Loader2 size={12} className="animate-spin" /> {t('prescriptionSection.picker_searching', 'Searching your medicines…')}
                </p>
            );
        }

        const loadMore = catalogueHasMore && (
            <div className="p-2 text-center border-t border-gray-50">
                {loadMoreError && (
                    <p className="text-[11px] text-red-600 mb-1">
                        {serverReason(loadMoreError, t('prescriptionSection.picker_load_more_failed', 'More medicines could not be loaded.'), lang)}
                    </p>
                )}
                <button
                    type="button"
                    onClick={() => fetchCatalogue(medicinesPage + 1)}
                    disabled={catalogueLoadingMore}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-50"
                    data-testid="prescription-picker-load-more"
                >
                    {catalogueLoadingMore && <Loader2 size={12} className="animate-spin" />}
                    {t('prescriptionSection.picker_load_more', 'Show more medicines')}
                </button>
            </div>
        );

        if (availableMaster.length > 0) {
            return (
                <>
                    {availableMaster.map((m) => (
                        <button
                            key={m.medicineId}
                            data-testid={`prescription-medicine-option-${m.medicineId}`}
                            type="button"
                            onClick={() => handlePick(m)}
                            className="w-full text-left p-2 hover:bg-[#4ABDBD]/5 border-b border-gray-50 last:border-b-0 cursor-pointer"
                        >
                            <div className="flex items-center gap-2 flex-wrap">
                                {m.type === 'product' ? (
                                    <Package size={13} className="text-amber-500 shrink-0" />
                                ) : (
                                    <Pill size={13} className="text-[#4ABDBD] shrink-0" />
                                )}
                                <span className="font-medium text-gray-800 text-sm">{m.name}</span>
                                {m.brand && (
                                    <span className="text-xs text-gray-500">{m.brand}</span>
                                )}
                                {m.type !== 'product' && m.dose && (
                                    <span className="text-xs text-gray-500">• {m.dose}</span>
                                )}
                                {m.type !== 'product' && m.isImportant && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                        <AlertCircle size={9} /> {t('prescriptionSection.important', 'Important')}
                                    </span>
                                )}
                                {m.pharmacy && (
                                    <span
                                        data-testid={`prescription-medicine-stock-${m.medicineId}`}
                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                            m.pharmacy.status === 'discontinued'
                                                ? 'bg-red-50 text-red-600'
                                                : m.pharmacy.stock === 'in'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        {m.pharmacy.status === 'discontinued'
                                            ? t('prescriptionSection.pharmacy_discontinued', 'No longer in the pharmacy')
                                            : m.pharmacy.stock === 'in'
                                            ? t('prescriptionSection.in_stock', 'In stock')
                                            : t('prescriptionSection.out_of_stock', 'Out of stock')}
                                    </span>
                                )}
                            </div>
                        </button>
                    ))}
                    {loadMore}
                </>
            );
        }

        if (medicines.length > 0) {
            return (
                <>
                    <p className="text-center text-xs text-gray-500 py-4">
                        {t('prescriptionSection.picker_all_added', 'All medicines already added.')}
                    </p>
                    {loadMore}
                </>
            );
        }

        if (searchSettled && trimmedSearch) {
            return (
                <div className="p-3 text-center space-y-2" data-testid="prescription-picker-no-match">
                    <p className="text-xs text-gray-500">
                        {t('prescriptionSection.picker_no_match', 'No medicine in your catalogue matches “{{query}}”.', { query: trimmedSearch })}
                    </p>
                    {!canTypeMedicine ? (
                        <p className="text-xs text-gray-500" data-testid="prescription-picker-pick-hint">
                            {t('prescriptionSection.picker_pick_hint', 'Pick it from the pharmacy on the Medicines page first.')}
                        </p>
                    ) : (
                    <button
                        type="button"
                        onClick={openInlineAdd}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold hover:bg-[#4ABDBD]/20 cursor-pointer"
                        data-testid="prescription-inline-add-btn"
                    >
                        <Plus size={12} />
                        {t('prescriptionSection.inline_add_btn', 'Add “{{name}}” to catalogue and prescribe', { name: trimmedSearch })}
                    </button>
                    )}
                </div>
            );
        }

        return (
            <p className="text-center text-xs text-gray-500 py-4 px-3" data-testid="prescription-picker-catalogue-empty">
                {canTypeMedicine
                    ? t('prescriptionSection.picker_catalogue_empty', 'Your medicine catalogue is empty. Add medicines on the Medicines page, or type a name here to add one.')
                    : t('prescriptionSection.picker_catalogue_empty_pharmacy', 'Your medicine list is empty. Pick medicines from the pharmacy on the Medicines page.')}
            </p>
        );
    };

    return (
        <div className="flex flex-col w-full gap-3 mb-4 sm:mb-6" data-testid="prescription-section">
            <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                {t('prescriptionSection.heading', 'Treatment Plan – Medicines')}
            </h2>
            {!readOnly && (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        {addMode ? (
                            <div ref={pickerRef} className="relative w-64 sm:w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    data-testid="prescription-search-input"
                                    ref={inputRef}
                                    type="text"
                                    value={addSearch}
                                    onChange={(e) => { setAddSearch(e.target.value.replace(/[<>]/g, '')); setInlineForm(null); }}
                                    placeholder={t('prescriptionSection.search_placeholder', 'Search by name, brand or purpose')}
                                    className="w-full pl-8 pr-8 py-1.5 rounded-xl border border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 text-sm bg-white"
                                />
                                <button
                                    data-testid="prescription-search-close"
                                    type="button"
                                    onClick={closePicker}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 cursor-pointer"
                                    aria-label={t('prescriptionSection.close', 'Close')}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setAddMode(true)}
                                disabled={listBlocked}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs sm:text-sm font-semibold shadow hover:shadow-md transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                aria-label={t('prescriptionSection.add_btn', 'Add')}
                                data-testid="prescription-add-btn"
                            >
                                <Plus size={14} /> {t('prescriptionSection.add_btn', 'Add')}
                            </button>
                        )}
                    </div>
                    {(savedLocked ? newRows.length > 0 : rows.length > 0) && (
                        <button
                            type="button"
                            onClick={handleRemoveAll}
                            disabled={removingAll || saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs sm:text-sm font-semibold hover:bg-red-100 hover:border-red-300 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="prescription-remove-all-btn"
                        >
                            {removingAll ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Trash2 size={14} />
                            )}
                            {removingAll
                                ? t('prescriptionSection.removing_all', 'Removing...')
                                : t('prescriptionSection.remove_all', 'Remove All')}
                        </button>
                    )}
                </div>
            )}

            {!readOnly && savedLocked && persistedRows.length > 0 && (
                <p className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="prescription-locked-note">
                    <AlertCircle size={12} className="shrink-0 text-gray-400" />
                    {t('prescriptionSection.locked_note', 'This visit is completed: the medicines already saved can no longer be changed or removed. You can still add new ones.')}
                </p>
            )}

            <div className="rounded-xl border border-gray-200 overflow-visible">
                <div className="overflow-x-auto overflow-y-visible">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">
                                    {t('prescriptionSection.col_medicine', 'Medicine')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">{countLabels.morningCount}</th>
                                <th className="px-3 py-2 text-center font-medium">{countLabels.afternoonCount}</th>
                                <th className="px-3 py-2 text-center font-medium">{countLabels.eveningCount}</th>
                                <th className="px-3 py-2 text-center font-medium">{countLabels.nightCount}</th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_food', 'BF / AF')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_days', 'Days')}
                                </th>
                                {!readOnly && <th className="px-3 py-2"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {listLoading && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.loading', 'Loading...')}
                                    </td>
                                </tr>
                            )}
                            {listFailed && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6">
                                        <div role="alert" className="flex flex-col items-center gap-2 text-center" data-testid="prescription-load-error">
                                            <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                                                <AlertTriangle size={15} />
                                                {t('prescriptionSection.load_failed', 'This prescription could not be loaded.')}
                                            </p>
                                            <p className="text-xs text-red-600" data-testid="prescription-load-error-reason">
                                                {serverReason(prescriptionsError, t('prescriptionSection.load_failed_hint', 'Check your connection and try again.'), lang)}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={retryList}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50 transition cursor-pointer"
                                                data-testid="prescription-retry-btn"
                                            >
                                                <RotateCw size={13} /> {t('prescriptionSection.retry', 'Retry')}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!listLoading && !listFailed && rows.length === 0 && readOnly && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.empty', 'No medicines prescribed yet.')}
                                    </td>
                                </tr>
                            )}
                            {!listLoading && !listFailed && rows.map((row) => {
                                const key = row._key;
                                // A saved line on a completed visit is read-only.
                                const rowReadOnly = readOnly || (savedLocked && !row._isNew);
                                const isDirty = !row._isNew && !savedLocked && !!drafts[key];
                                const needsDose = !readOnly && (row._isNew || isDirty) && !isComplete(row);
                                const entryMode = modeOf(row);
                                // Schedule cells span 6 columns (Morning, Afternoon, Evening,
                                // Night, BF/AF, Days). In T mode we replace them with a single
                                // colSpan=6 cell containing a resizable textarea.
                                const textColSpan = 6;
                                return (
                                    <tr key={key} data-testid={`prescription-row-${key}`} className={`border-t border-gray-100 ${row._isNew ? 'bg-[#4ABDBD]/5' : isDirty ? 'bg-amber-50/40' : ''}`}>
                                        <td className="px-3 py-3 align-top">
                                            <div className="flex items-start gap-2">
                                                {row.type === 'product' ? (
                                                    <Package size={14} className="text-amber-500 mt-1" />
                                                ) : (
                                                    <Pill size={14} className="text-[#4ABDBD] mt-1" />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-medium text-gray-800">{row.medicineName}</span>
                                                        {row.isImportant && (
                                                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                                                <AlertCircle size={10} /> {t('prescriptionSection.important', 'Important')}
                                                            </span>
                                                        )}
                                                        {row._isNew && (
                                                            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-[#4ABDBD]/15 text-[#2D8A8A]" data-testid={`prescription-row-new-${key}`}>
                                                                {t('prescriptionSection.row_new', 'New — not saved')}
                                                            </span>
                                                        )}
                                                        {isDirty && (
                                                            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title={t('prescriptionSection.row_dirty_tooltip', 'Has unsaved changes')}>
                                                                {t('prescriptionSection.row_dirty', 'Unsaved')}
                                                            </span>
                                                        )}
                                                        {needsDose && (
                                                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700" data-testid={`prescription-row-needs-dose-${key}`}>
                                                                <AlertTriangle size={10} /> {t('prescriptionSection.row_needs_dose', 'Set when to take it')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {(row.brand || row.dose) && (
                                                        <div className="text-xs text-gray-500">
                                                            {row.brand}{row.brand && row.dose ? ' • ' : ''}{row.dose}
                                                        </div>
                                                    )}
                                                    {/* S/T mode toggle — staged in drafts, persisted on Save Changes. */}
                                                    <div className="inline-flex rounded-full bg-gray-100 p-0.5 mt-2" data-testid={`prescription-entry-mode-toggle-${key}`}>
                                                        {['S', 'T'].map((opt) => (
                                                            <button
                                                                key={opt}
                                                                type="button"
                                                                data-testid={`prescription-entry-mode-${opt}-${key}`}
                                                                disabled={rowReadOnly || saving}
                                                                onClick={() => {
                                                                    if (entryMode === opt) return;
                                                                    setDraft(row, { entryMode: opt });
                                                                }}
                                                                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition cursor-pointer ${
                                                                    entryMode === opt
                                                                        ? 'bg-[#4ABDBD] text-white'
                                                                        : 'text-gray-600'
                                                                } disabled:cursor-not-allowed`}
                                                                title={opt === 'S'
                                                                    ? t('prescriptionSection.entry_mode_s_tooltip', 'Schedule — show morning/afternoon/evening/night, BF·AF and days')
                                                                    : t('prescriptionSection.entry_mode_t_tooltip', 'Text content — hide schedule cells and show a free-form note for this row')}
                                                            >
                                                                {opt}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        {entryMode === 'T' ? (
                                            <td colSpan={textColSpan} className="px-2 py-3">
                                                <textarea
                                                    value={notesDraft[key] ?? valueOf(row, 'notes') ?? ''}
                                                    disabled={rowReadOnly || saving}
                                                    onChange={(e) => setNotesDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                                                    onBlur={() => commitNotesChange(row)}
                                                    placeholder={t('prescriptionSection.text_placeholder', 'Type the instructions for this entry…')}
                                                    rows={2}
                                                    className="w-full text-sm rounded-lg border border-gray-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 disabled:cursor-not-allowed disabled:bg-gray-50 resize-y min-h-[2.5rem]"
                                                    data-testid={`prescription-row-notes-${key}`}
                                                    aria-label={t('prescriptionSection.entry_mode_t_label', 'Text content')}
                                                />
                                            </td>
                                        ) : (
                                            <>
                                                {/* Counters render for products too — products carry the same
                                                    morning/afternoon/evening/night fields and the row is treated
                                                    identically to a medicine in S mode. */}
                                                {COUNT_FIELDS.map((field) => (
                                                    <td key={field} className="px-2 py-3 text-center">
                                                        <Counter
                                                            value={valueOf(row, field)}
                                                            label={countLabels[field]}
                                                            disabled={rowReadOnly || saving}
                                                            onChange={(v) => setDraft(row, { [field]: v })}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-2 py-3 text-center">
                                                    <div className="inline-flex rounded-full bg-gray-100 p-0.5">
                                                        {['BF', 'AF'].map((opt) => (
                                                            <button
                                                                key={opt}
                                                                data-testid={`patient-prescription-food-timing-${opt}-${key}`}
                                                                type="button"
                                                                disabled={rowReadOnly || saving}
                                                                onClick={() => setDraft(row, { foodTiming: opt })}
                                                                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition cursor-pointer ${
                                                                    valueOf(row, 'foodTiming') === opt
                                                                        ? 'bg-[#4ABDBD] text-white'
                                                                        : 'text-gray-600'
                                                                } disabled:cursor-not-allowed`}
                                                                title={opt === 'BF'
                                                                    ? t('prescriptionSection.before_food', 'Before Food')
                                                                    : t('prescriptionSection.after_food', 'After Food')}
                                                            >
                                                                {opt}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3 text-center">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={MAX_DURATION}
                                                        value={daysDraft[key] ?? valueOf(row, 'durationDays') ?? DEFAULT_DURATION}
                                                        disabled={rowReadOnly || saving}
                                                        onChange={(e) => setDaysDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                                                        onBlur={() => commitDaysChange(row)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                                        className="w-14 text-center text-sm font-semibold rounded-lg border border-gray-200 px-1 py-1 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 disabled:cursor-not-allowed disabled:bg-gray-50"
                                                        data-testid={`prescription-row-days-${key}`}
                                                        aria-label={t('prescriptionSection.col_days', 'Days')}
                                                    />
                                                </td>
                                            </>
                                        )}
                                        {!readOnly && (
                                            <td className="px-3 py-3 text-center align-top">
                                                {!rowReadOnly && (
                                                <button
                                                    data-testid={`prescription-remove-button-${key}`}
                                                    type="button"
                                                    onClick={() => handleRemove(row)}
                                                    disabled={saving}
                                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionSection.remove', 'Remove')}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}

                            {!readOnly && !listLoading && !listFailed && rows.length === 0 && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.add_hint_empty', 'Click + to add the first medicine')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save / Discard bar — row edits and new (draft) rows buffer locally
                and only hit the API when the doctor clicks Save Changes. Hidden
                in readOnly so it never appears in patient views. */}
            {!readOnly && (
                <div className="flex items-center justify-end gap-2 flex-wrap" data-testid="prescription-save-bar">
                    <DraftChip
                        section={PRESCRIPTION_DRAFT_SECTION}
                        chip={rxDraft.chips[PRESCRIPTION_DRAFT_SECTION]}
                        onDiscard={handleDiscardDraft}
                        disabled={saving}
                    />
                    {pendingCount > 0 && (
                        <span className="text-xs text-amber-700 mr-auto" data-testid="prescription-dirty-count">
                            {t('prescriptionSection.dirty_count', '{{n}} row(s) with unsaved changes', { n: pendingCount })}
                        </span>
                    )}
                    {incompleteRows.length > 0 && (
                        <p className="basis-full text-xs text-orange-700 flex items-center gap-1.5" data-testid="prescription-incomplete-hint">
                            <AlertTriangle size={12} className="shrink-0" />
                            {t('prescriptionSection.dose_required', 'Set at least one dose time — or switch to T and write the instructions — for: {{names}}', {
                                names: incompleteRows.map((r) => r.medicineName).join(', '),
                            })}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={handleDiscardAll}
                        disabled={saving || pendingCount === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-xs sm:text-sm font-semibold hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prescription-discard-changes-btn"
                    >
                        {t('prescriptionSection.discard_changes', 'Discard Changes')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveAll}
                        disabled={!canSave}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs sm:text-sm font-semibold shadow hover:shadow-md transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prescription-save-changes-btn"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                {t('prescriptionSection.saving', 'Saving…')}
                            </>
                        ) : (
                            t('prescriptionSection.save_changes', 'Save Changes')
                        )}
                    </button>
                </div>
            )}

            {/* Legend — explains the single-letter labels used in the table
                (entry-mode toggle + food-timing pill). */}
            <div
                className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500"
                data-testid="prescription-legend"
            >
                <span><span className="font-semibold text-gray-700">S</span> — {t('prescriptionSection.legend_s', 'Schedule')}</span>
                <span><span className="font-semibold text-gray-700">T</span> — {t('prescriptionSection.legend_t', 'Text content')}</span>
                <span><span className="font-semibold text-gray-700">BF</span> — {t('prescriptionSection.before_food', 'Before Food')}</span>
                <span><span className="font-semibold text-gray-700">AF</span> — {t('prescriptionSection.after_food', 'After Food')}</span>
            </div>

            {addMode && dropdownPos && createPortal(
                <div
                    ref={dropdownRef}
                    data-testid="prescription-picker-dropdown"
                    style={{
                        position: 'fixed',
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        top: dropdownPos.top,
                        bottom: dropdownPos.bottom,
                        maxHeight: dropdownPos.maxHeight,
                        zIndex: 1000,
                    }}
                    className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-y-auto"
                >
                    {renderPickerBody()}
                </div>,
                document.body
            )}
        </div>
    );
}

export default PrescriptionSection;
